/**
 * Since design-system removed the support for `useMarkdown` in Banner,
 * we have to generate translate the markdown texts into Design System Components ourselves.
 * Referenced from design-system's example code: https://github.com/opengovsg/design-system/blob/240ae0ec72fd89b06c3328255ca0aa625d835459/react/examples/generateMarkdownContent.md
 */

import { Components } from 'react-markdown'
import {
  LinkProps,
  ListItem,
  ListProps,
  OrderedList,
  SystemStyleObject,
  Text,
  TextProps,
  UnorderedList,
} from '@chakra-ui/react'
import { Link } from '@opengovsg/design-system-react'

export type MdComponentProps = {
  /**
   * If exists, will be passed into Link component
   */
  link?: LinkProps & {
    /**
     * If exists, will be used to determine if link is an external link.
     * If not provided, and if @param `ssr` is true, window.location.origin will be used to determine if link is external.
     */
    isExternalFn?: (href: string) => boolean
  }
  /**
   * If exists, will be passed into List related component
   */
  list?: ListProps
  /**
   * If exists, will be passed into Text related component
   */
  text?: TextProps
}

export type MdComponentStyles = {
  /**
   * If exists, will be used for styling links.
   */
  link?: SystemStyleObject
  /**
   * If exists, will be used for styling lists
   */
  list?: SystemStyleObject
  /**
   * If exists, will be used for styling text
   */
  text?: SystemStyleObject
}

interface UseMdComponentsProps {
  /**
   * Whether the component is being rendered in an SSR environment.
   * Set to true if rendering in a SSR environment.
   */
  ssr?: boolean
  styles?: MdComponentStyles
  props?: MdComponentProps
  overrides?: Components
}

const calcIsExternal = ({
  href,
  ssr,
  isExternalFn,
}: {
  href: unknown
  ssr?: boolean
  isExternalFn?: (href: string) => boolean
}) => {
  if (typeof href !== 'string') {
    return false
  }
  if (isExternalFn) {
    return isExternalFn?.(href)
  }
  if (ssr) {
    return false
  }
  return (
    typeof window !== 'undefined' && !href.startsWith(window.location.origin)
  )
}

/**
 * Generates the React Components which can be composed from parsing markdown texts.
 * @param styles Optional CSS stylings for the generated Components
 * @param props Optional props for the generated Components
 * @param overrides Optional Components to override the defaults
 * @param ssr Whether the Components should be generated in an SSR environment
 * @returns Components to be passed into `components` attribute in <ReactMarkdown>
 */
export const generateMarkdownComponents = ({
  styles = {},
  props = {},
  overrides = {},
  ssr,
}: UseMdComponentsProps = {}): Components => {
  const textStyles = styles.text

  const linkStyles = {
    whiteSpace: 'pre-wrap',
    display: 'initial',
    ...(styles.link ?? {}),
  }

  const listStyles = {
    whiteSpace: 'pre-wrap',
    ...(styles.list ?? {}),
  }

  const mdComponents: Components = {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ol: ({ node, ordered, ...rest }) => (
      <OrderedList
        marginInlineStart="1.25rem"
        whiteSpace="initial"
        sx={textStyles}
        {...props.list}
        {...rest}
      />
    ),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ul: ({ node, ordered, ...rest }) => (
      <UnorderedList {...props.list} {...rest} sx={listStyles} />
    ),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    li: ({ node, ordered, ...rest }) => <ListItem {...rest} sx={textStyles} />,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    a: ({ node, href, ...rest }) => {
      const { isExternalFn, ...restLinkProps } = props.link ?? {}
      const isExternal = calcIsExternal({ href, ssr, isExternalFn })
      return (
        <Link
          isExternal={isExternal}
          href={href}
          sx={linkStyles}
          {...rest}
          {...restLinkProps}
        />
      )
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    p: ({ node, ...props }) => <Text {...props} sx={textStyles} />,
    ...overrides,
  }

  return mdComponents
}
